import React from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Text } from 'components/ui';
import Checkbox from 'components/Inputs/Checkbox/Checkbox';
import { wrapFunctionalComponent } from 'utils/framework';
import { space, forms } from 'style/config';
import anaConsts from 'analytics/constants';
import * as pdpAnalytics from 'analytics/productPage/pdpAnalytics';
import basketUtils from 'utils/Basket';
import deliveryFrequency from 'utils/DeliveryFrequency';
import { isChangeQuantityEnabled } from 'utils/arChangeQuantityFlag';
import languageLocale from 'utils/LanguageLocale';
import AutoReplenishDetails from 'components/ProductPage/FulfillmentMethods/AutoReplenishDetails';
import FulfillmentMethodsEN from 'components/ProductPage/FulfillmentMethods/locales/FulfillmentMethods_en_US';
import FulfillmentMethodsFR from 'components/ProductPage/FulfillmentMethods/locales/FulfillmentMethods_fr_CA';

const { formatSavingAmountString, formatCurrency } = deliveryFrequency;
const getText = languageLocale.getLocalisedDataFromResource(FulfillmentMethodsEN, FulfillmentMethodsFR);

const PER_UNIT_QUANTITY = 1;
// Indent the expanded details so they align under the row title (checkbox box + gap),
// matching the Figma layout where everything sits in the column beside the checkbox.
const DETAILS_INDENT = forms.RADIO_SIZE + space[2];

const styles = {
    checkbox: {
        '.Checkbox-box': { marginRight: space[2] }
    },
    // Reset the shared Checkbox baseline nudge (-.0625em) so the single-line ineligible
    // label centers with the disabled box (SHP-5407). The disabled slash, #ccc border and
    // #f6f6f8 fill come from the shared Checkbox disabled treatment.
    checkboxIneligible: {
        '.Checkbox-box': { marginTop: 0 }
    },
    // Mobile: title and price stack (space[1] / 2 apart). Desktop: title left, price right.
    titleRow: {
        columnGap: space[2],
        rowGap: space[1] / 2
    },
    price: {
        whiteSpace: 'nowrap'
    },
    details: {
        marginLeft: DETAILS_INDENT
    }
};

/**
 * Renders the label content for the enabled (in-stock) Auto-Replenish row. On a Hot
 * Deal the copy swaps to the Hot Deal wording, which carries the fine print asterisk
 * on the savings rather than on the discounted price (SHP-5422).
 */
const renderEnabledLabel = (savingsDiscount, showPrice, adjusterPrice, displayQty, isHotDeal, listPrice) => (
    <Flex
        width='100%'
        flexDirection={['column', 'row']}
        justifyContent={['flex-start', 'space-between']}
        alignItems={['flex-start', 'baseline']}
        css={styles.titleRow}
    >
        {savingsDiscount && (
            <Box>
                <Text
                    is='span'
                    fontSize='sm'
                    fontWeight='bold'
                    color='red'
                    children={getText(isHotDeal ? 'autoReplenishRowSavePercentHotDeal' : 'autoReplenishRowSavePercent', [savingsDiscount])}
                />
                <Text
                    is='span'
                    fontSize='sm'
                    fontWeight='bold'
                    color='black'
                    children={getText(isHotDeal ? 'autoReplenishRowSuffixHotDeal' : 'autoReplenishRowSuffix')}
                />
            </Box>
        )}
        {showPrice && (
            <Box css={styles.price}>
                <Text
                    is='span'
                    fontSize='sm'
                    fontWeight='bold'
                    color='red'
                    children={`${formatCurrency(adjusterPrice, displayQty)} `}
                />
                <Text
                    is='del'
                    fontSize='sm'
                    color='gray'
                    children={formatCurrency(listPrice, displayQty)}
                />
            </Box>
        )}
    </Flex>
);

/**
 * Renders the label content for the out-of-stock Auto-Replenish row (red discount, gray suffix).
 * A Hot Deal keeps its own copy while out of stock so the wording does not change with
 * stock status (SHP-5422).
 */
const renderOutOfStockLabel = (savingsDiscount, isHotDeal) => (
    <Box>
        <Text
            is='span'
            fontSize='sm'
            fontWeight='bold'
            color='red'
            children={getText(isHotDeal ? 'autoReplenishRowSavePercentHotDeal' : 'autoReplenishRowSavePercent', [savingsDiscount])}
        />
        <Text
            is='span'
            fontSize='sm'
            fontWeight='bold'
            color='gray'
            children={getText(isHotDeal ? 'autoReplenishRowSuffixHotDeal' : 'autoReplenishRowSuffix')}
        />
    </Box>
);

/**
 * Renders the label content for the ineligible Auto-Replenish row.
 */
const renderIneligibleLabel = () => (
    <Text
        is='span'
        fontSize='sm'
        color='gray'
        children={getText('autoReplenishRowNotEligible')}
    />
);

/**
 * AutoReplenishRow renders the "Save 5% with Auto-Replenish" checkbox beneath
 * the shipping details panel.
 *
 * SHP-5122 makes the checkbox interactive: it is a controlled input bound to the
 * shared `commerce` Auto-Replenish state (from `useCommerce`), so toggling it
 * reproduces the exact same update logic that clicking the legacy AR delivery
 * card does today — no backend or breaking changes, UI only. Because the state
 * is shared with the commerce/basket flow, enrolling here carries through to
 * add-to-basket just like the legacy layout.
 *
 * When checked and the current SKU is replenishment-eligible, the discounted
 * price appears on the title row (right-aligned on desktop, stacked below the
 * title on mobile) and the expanded `AutoReplenishDetails` (delivery frequency
 * selector, consent copy, "Learn more") renders below, collapsing on uncheck.
 * When the current SKU is not eligible the checkbox is disabled and its inline
 * label is replaced by the gray "Not eligible for Auto-Replenish" copy per Figma
 * (SHP-5139). When no SKU within the product is eligible the row is not rendered
 * at all (gated by `showAutoReplenishment` in the parent).
 *
 * When the item is out of stock for standard shipping, the checkbox is also
 * disabled and the text appears muted (gray) per acceptance criteria.
 *
 * When the SKU carries an `acceleratedPromotion` it is on an Auto-Replenish Hot
 * Deal, which swaps the row copy to the Hot Deal wording and moves the fine print
 * asterisk off the discounted price and onto the savings (SHP-5422).
 *
 * @param {object} props
 * @param {object} props.currentSku - The current SKU (drives pricing/eligibility).
 * @param {object} props.commerce - Shared Auto-Replenish state from useCommerce.
 * @param {boolean} props.isReplenishmentEligible - Whether the current SKU is AR-eligible.
 * @returns {JSX.Element}
 */
const AutoReplenishRow = ({ currentSku, commerce = {}, isReplenishmentEligible }) => {
    const { autoReplenishChecked, autoReplenishClick, quantity } = commerce;

    const { acceleratedPromotion, replenishmentAdjuster } = currentSku || {};
    const listPrice = parseFloat(basketUtils.removeCurrency(currentSku?.listPrice));
    // Discounted (Auto-Replenish) price. Mirrors the field resolution in
    // `formatSavingAmountString` (replenishmentAdjusterPrice, falling back to discountedPrice).
    const adjusterPrice = parseFloat(basketUtils.removeCurrency(currentSku?.replenishmentAdjusterPrice || currentSku?.discountedPrice));
    const displayQty = isChangeQuantityEnabled() && autoReplenishChecked ? Number(quantity) || PER_UNIT_QUANTITY : PER_UNIT_QUANTITY;
    const showPrice = Boolean(autoReplenishChecked && replenishmentAdjuster);
    // The savings can be any percentage (5%, 10%, 15%, …) or a dollar amount depending
    // on the SKU's replenishment adjuster, so derive it instead of hardcoding "5%".
    // `displayDiscountOnly` returns just the discount value ("5%" / "$5.00").
    const savingsDiscount = replenishmentAdjuster ? formatSavingAmountString(currentSku, PER_UNIT_QUANTITY, true) : '';
    // An accelerated promotion means the SKU is on an Auto-Replenish Hot Deal, which swaps
    // the row copy and moves the fine-print asterisk onto the savings (SHP-5422).
    const isHotDeal = Boolean(acceleratedPromotion);

    const fireAutoReplenishClickAnalytics = () => {
        digitalData.page.attributes.outOfStock = currentSku?.isOutOfStock ? `Yes - ${anaConsts.FULFILLMENT_TYPE.AutoReplenish}` : '';

        pdpAnalytics.trackLinkClick({
            actionInfo: anaConsts.EVENT_NAMES.AUTO_REPLENISH_RADIO_BUTTON_CLICK,
            prop44: digitalData.page.attributes.outOfStock,
            productStrings: `;${currentSku?.skuId};;;;eVar26=${currentSku?.skuId}`
        });
    };

    const handleChange = event => {
        const isChecked = event.target.checked;

        autoReplenishClick?.(isChecked);

        if (isChecked) {
            fireAutoReplenishClickAnalytics();
        }
    };

    const isOutOfStock = currentSku?.isOutOfStock;

    // Checkbox is disabled when either the SKU is not replenishment-eligible
    // or the item is out of stock for standard shipping.
    const isDisabled = !isReplenishmentEligible || isOutOfStock;

    // Determine which label to render based on eligibility and stock status
    const isEligibleAndInStock = isReplenishmentEligible && !isOutOfStock;
    const isEligibleButOutOfStock = isReplenishmentEligible && isOutOfStock && savingsDiscount;

    let labelContent;

    if (isEligibleAndInStock) {
        labelContent = renderEnabledLabel(savingsDiscount, showPrice, adjusterPrice, displayQty, isHotDeal, listPrice);
    } else if (isEligibleButOutOfStock) {
        labelContent = renderOutOfStockLabel(savingsDiscount, isHotDeal);
    } else {
        labelContent = renderIneligibleLabel();
    }

    return (
        <>
            <Checkbox
                name='fulfillmentMethodsAutoReplenish'
                checked={Boolean(autoReplenishChecked)}
                disabled={isDisabled}
                onChange={handleChange}
                // Ineligible: omit aria-label so Checkbox keeps the visible label announced; an
                // aria-label would make it aria-hide the "Not eligible…" text (SHP-5371).
                aria-label={
                    isEligibleAndInStock
                        ? getText(isHotDeal ? 'autoReplenishRowCheckboxAriaLabelHotDeal' : 'autoReplenishRowCheckboxAriaLabel', [savingsDiscount])
                        : undefined
                }
                width='100%'
                alignItems={isEligibleAndInStock ? 'flex-start' : 'center'}
                css={[styles.checkbox, isDisabled && styles.checkboxIneligible]}
            >
                {labelContent}
            </Checkbox>
            {isReplenishmentEligible && autoReplenishChecked && (
                <Box css={styles.details}>
                    <AutoReplenishDetails
                        currentSku={currentSku}
                        commerce={commerce}
                    />
                </Box>
            )}
        </>
    );
};

AutoReplenishRow.propTypes = {
    currentSku: PropTypes.object,
    commerce: PropTypes.object,
    isReplenishmentEligible: PropTypes.bool
};

export default wrapFunctionalComponent(AutoReplenishRow, 'AutoReplenishRow');
